/**
 * Modal pour déplacer un ou plusieurs étudiants d'une salle à une autre
 * (post-brassage).
 *
 * Usage type :
 *   - Depuis la vue "Résultats par salle" : l'utilisateur sélectionne 1+
 *     étudiants via checkbox, puis clique "Déplacer".
 *   - Le modal affiche les étudiants concernés, propose la liste des salles
 *     disponibles avec places restantes, et applique le déplacement en 1 appel.
 *
 * Contrôles côté client :
 *   - Vérifie que la salle destination a assez de places libres.
 *   - Bloque le déplacement vers la salle actuelle (no-op).
 *   - Motif optionnel enregistré dans la description de la session.
 */
import React, { useMemo, useState } from 'react';
import { X, ArrowRight, Building2, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  useGetSallesDisponiblesQuery,
  useDeplacerEtudiantsBrassageMutation,
  PlacementEtudiant,
} from '../../services/brassageApi';

interface Props {
  sessionId: number;
  placements: PlacementEtudiant[];       // Étudiants à déplacer (déjà sélectionnés)
  onClose: () => void;
  onSuccess?: (nbDeplaces: number) => void;
}

const DeplacerEtudiantsModal: React.FC<Props> = ({ sessionId, placements, onClose, onSuccess }) => {
  const { data: sallesData, isLoading: loadingSalles } = useGetSallesDisponiblesQuery({ sessionId });
  const [deplacer, { isLoading: deplacing }] = useDeplacerEtudiantsBrassageMutation();
  const [salleId, setSalleId] = useState<number | ''>('');
  const [motif, setMotif] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Salle actuelle de la sélection (si tous les étudiants sont dans la même)
  const sallesActuelles = new Set(placements.map(p => p.salle));
  const salleActuelleUnique = sallesActuelles.size === 1
    ? Array.from(sallesActuelles)[0]
    : null;

  // Salles disponibles : exclure la salle actuelle si tous du même endroit
  const sallesFiltrees = useMemo(() => {
    const list = sallesData ?? [];
    return list
      .filter(s => !salleActuelleUnique || s.id !== salleActuelleUnique)
      .sort((a, b) => b.places_restantes - a.places_restantes);
  }, [sallesData, salleActuelleUnique]);

  const salleChoisie = sallesFiltrees.find(s => s.id === salleId);
  const capaciteOK = salleChoisie ? placements.length <= salleChoisie.places_restantes : false;

  const handleDeplacer = async () => {
    if (!salleId || !salleChoisie) return;
    if (!capaciteOK) {
      setMsg({ type: 'error', text: `Capacité insuffisante — ${placements.length} étudiants à déplacer, ${salleChoisie.places_restantes} place(s) libre(s).` });
      return;
    }
    setMsg(null);
    try {
      const res = await deplacer({
        sessionId,
        placement_ids: placements.map(p => p.id),
        salle_id_destination: salleId,
        motif: motif.trim() || undefined,
      }).unwrap();
      setMsg({ type: 'success', text: res.message });
      onSuccess?.(res.nb_deplaces);
      // Fermer après 1.5s pour laisser voir le message
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.data?.error || err?.data?.message || 'Erreur lors du déplacement.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto"
         onClick={onClose}>
      <div className="my-8 w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-2xl"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <ArrowRight size={16} /> Déplacer {placements.length} étudiant{placements.length > 1 ? 's' : ''} vers une autre salle
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Étudiants sélectionnés (résumé) */}
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Étudiants concernés</div>
            <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2 text-xs bg-gray-50 dark:bg-gray-700/40">
              {placements.map(p => (
                <div key={p.id} className="flex items-center justify-between py-0.5">
                  <span className="text-gray-800 dark:text-gray-200">
                    <span className="font-semibold">{p.etudiant_nom}</span> {p.etudiant_prenoms}
                    <span className="ml-2 text-gray-500">({p.etudiant_classe})</span>
                  </span>
                  <span className="text-gray-500">{p.site_nom} / {p.salle_nom}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Salle destination */}
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 block">
              Salle destination
            </label>
            {loadingSalles ? (
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Chargement des salles…
              </div>
            ) : sallesFiltrees.length === 0 ? (
              <div className="text-xs text-red-600 flex items-center gap-2">
                <AlertTriangle size={12} /> Aucune autre salle disponible dans cette session.
              </div>
            ) : (
              <select
                value={salleId}
                onChange={e => setSalleId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">— Choisir une salle —</option>
                {sallesFiltrees.map(s => (
                  <option key={s.id} value={s.id} disabled={s.places_restantes < placements.length}>
                    {s.site_nom} / {s.nom} — {s.places_restantes} / {s.capacite} place{s.places_restantes > 1 ? 's libres' : ' libre'}
                    {s.places_restantes < placements.length ? ' (insuffisant)' : ''}
                  </option>
                ))}
              </select>
            )}
            {salleChoisie && (
              <div className={`mt-2 flex items-center gap-2 text-xs ${capaciteOK ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                <Building2 size={12} />
                {capaciteOK
                  ? `${salleChoisie.places_restantes} place(s) libre(s) — OK pour ${placements.length} étudiant(s)`
                  : `Capacité insuffisante : ${salleChoisie.places_restantes} place(s) pour ${placements.length} étudiant(s)`}
              </div>
            )}
          </div>

          {/* Motif optionnel */}
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 block">
              Motif (optionnel — sera enregistré dans l'historique de la session)
            </label>
            <input
              type="text"
              value={motif}
              onChange={e => setMotif(e.target.value)}
              placeholder="Ex: équilibrage, salle en panne, demande étudiant…"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              maxLength={250}
            />
          </div>

          {/* Message */}
          {msg && (
            <div className={`p-2 rounded text-xs flex items-start gap-2 ${
              msg.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {msg.type === 'success' ? <CheckCircle size={12} className="mt-0.5" /> : <AlertTriangle size={12} className="mt-0.5" />}
              {msg.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={handleDeplacer}
            disabled={!salleId || !capaciteOK || deplacing}
            className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {deplacing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            Déplacer
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeplacerEtudiantsModal;
